import {
	observable,
	action,
	computed,
	reaction,
	IReactionDisposer,
	runInAction,
} from "mobx";
import { Subscription } from "rxjs";

import { ISubscriptionRequest, Channels } from "@gp/hub";
import {
	BettingType,
	IDividedBettingTypesResult,
	OfferSubscriptionResponse,
	Sport,
	Team,
	Tournament,
} from "@gp/models";
import { Sorter, insert } from "@gp/utility";
import { EventOffer } from "@gp/offer";

import { IStoreLifeCycle } from "@interface";
import betRules from "@betting-rules";

import { LoaderStore } from "@state/stores/common";
import { PeriodConverter, redirect, getCurrentLiveSection } from "@state";

import { LiveOfferMenuStore } from "@state/stores/offer/live/LiveOfferMenuStore";
import FavoritesStore from "@state/stores/offer/shared/FavoritesStore";
import MultiEventViewStore from "@state/stores/offer/live/MultiEventViewStore";
import SportStatisticsStore from "../statistics/SportStatisticsStore";
import type { RootStore } from "@state/stores/types";
import LivePlayerStatisticsStore from "../statistics/LivePlayerStatisticsStore";
import TeamStandingsStatsStore from "../statistics/TeamStandingsStatsStore";
import TeamLineupsStore from "../statistics/TeamLineupsStore";
import {
	EventBodyTabs,
	StatisticsGroups,
	DEFAULT_SECTION,
} from "@data-types/offer/event/EventViewStoreEnums";

import { getCurrentCulture } from "@utils";
import { analyticsTracker } from "@utils/analytics";
import BetBoosterEventPickStore from "@state/quick-play-store/BetBoosterEventPickStore";
import { GroupOfferStore, Options } from "@state/stores/offer/GroupOfferStore";
import BettingTipsStore from "@state/quick-play-store/BettingTipsStore";
import { THEME_SETTINGS } from "@theme-settings";
import { IOfferService } from "../../../../data/services/offer/IOfferService";
import { offerHistoryService } from "../../../../data/services/offer/offer-history/OfferHistoryService";
import { prematchOfferService } from "../../../../data/services/offer/PrematchOfferService";
import { convertApiOfferResponseToHubModel } from "@utils/offer/convertApiOfferResponseToHubModel";
import { Period } from "@state/common/PeriodConverter";

export type TBettingTypeGroup = {
	name: string;
	abrv: string;
	id: string;
	sortOrder: 1;
	bettingTypes: BettingType[];
};
export default class BaseEventViewStore
	extends GroupOfferStore
	implements IStoreLifeCycle
{
	//#region observable

	@observable public isFetchingData = false;
	@observable public isStoreInitialized = false;
	@observable public isExpanded: boolean = false;
	@observable currentTab = EventBodyTabs.Markets;
	@observable displayStatisticsGroup: StatisticsGroups | null = null;

	@observable public eventIdsRaw = "";
	protected loader: LoaderStore;
	protected liveOfferMenuStore: LiveOfferMenuStore | undefined | null;

	public subscription: Subscription | null = null;
	public favoritesStore: FavoritesStore;
	favoritesBettingTypeReaction: IReactionDisposer | null;
	@observable favoritesBettingTypeGroups: any = {};
	@observable betBuilderAvailableTips: any = observable.map();
	multiEventViewStore: MultiEventViewStore | null;
	sportStatisticsStore: SportStatisticsStore;
	playerStatisticsStore: LivePlayerStatisticsStore;
	bettingTipsStore: BettingTipsStore;
	teamStandingsStatsStore: TeamStandingsStatsStore;
	teamLineupsStore: TeamLineupsStore;

	public storeName: "EventViewStore" = "EventViewStore";

	mainBettingTypes: IDividedBettingTypesResult | null = null;
	defaultBettingTypes;
	defaultBettingTypesCount = 0;

	@observable bettingTypeGroups = observable.array<TBettingTypeGroup>();
	@observable eventId: string;
	betBoosterEventPicks: BetBoosterEventPickStore;
	isLive: boolean = false;

	private requestVersion: number = 0;
	period: string = PeriodConverter.GetDefaultPeriod();
	@observable private _historyEventIds: Set<string> = new Set();
	//#endregion observable

	//#region computed

	@computed public get isMultiEvent() {
		return this.eventIds.length > 1;
	}

	@computed public get eventIds() {
		// slice ensures to remove first empty element because of leading '/'
		let currentIds = this.eventIdsRaw.split("/").slice(1);
		return currentIds.map((id) => id.split("-")[0]);
	}

	@computed public get myBetsToggleStateList() {
		const currentIdsList = this.eventIdsRaw.split("/").slice(1);

		return currentIdsList.map((id) => {
			const currentIdParams = id.split("-");

			return {
				eventId: currentIdParams[0],
				isMyBetsOpen: currentIdParams[1].split("=")[1] === "true",
			};
		});
	}

	@computed get firstAvailableStats() {
		if (this.sportStatisticsStore.isMatchStatsAvailable)
			return StatisticsGroups.LiveStats;

		if (this.sportStatisticsStore.isStreakStatAvailable)
			return StatisticsGroups.StreakStats;

		if (this.teamLineupsStore.isTeamLineupsAvailable)
			return StatisticsGroups.TeamLineups;

		if (this.playerStatisticsStore.isPlayerStatsAvailable)
			return StatisticsGroups.PlayerStats;

		if (this.sportStatisticsStore.isEventInsightsAvailable) {
			return StatisticsGroups.EventInsights;
		}
		if (this.teamStandingsStatsStore.isTeamStandingsStatsAvailable)
			return StatisticsGroups.TeamStandings;

		if (this.sportStatisticsStore.isFullStatsAvailable)
			return StatisticsGroups.FullStats;

		return null;
	}

	@computed public get isLoading() {
		return this.loader.isLoading;
	}

	@computed public get isEmpty() {
		return (
			this.eventsMap.size === 0 &&
			this.isStoreInitialized &&
			!this.isLoading &&
			!this.isFetchingData
		);
	}

	@computed public get betBuilderBettingType() {
		return this.bettingTypeGroups
			.map((bettingTypeGroup) => {
				const filtered = bettingTypeGroup.bettingTypes.filter(
					//@ts-expect-error
					(bettingType) => bettingType.betBuilder
				);

				return { ...bettingTypeGroup, bettingTypes: filtered };
			})
			.filter(
				(bettingTypeGroup) => bettingTypeGroup.bettingTypes.length > 0
			);
	}

	@computed public get defaultBettingTypeDisplay() {
		return this.bettingTypeGroups
			.map((bettingTypeGroup) => {
				const filtered = bettingTypeGroup.bettingTypes.filter(
					//@ts-expect-error
					(bettingType) => bettingType.isDefaultBettingType
				);

				return { ...bettingTypeGroup, bettingTypes: filtered };
			})
			.filter(
				(bettingTypeGroup) => bettingTypeGroup.bettingTypes.length > 0
			);
	}

	public isHistoryEvent(id: string): boolean {
		return this._historyEventIds.has(id);
	}

	@computed public get isOfferHistory(): boolean {
		return this._historyEventIds.size > 0;
	}
	//#endregion computed

	//#region constructor

	constructor(
		rootStore: RootStore,
		liveOfferMenuStore: LiveOfferMenuStore | undefined | null,
		favoritesStore: FavoritesStore,
		multiEventViewStore: MultiEventViewStore | null,
		options: Options = null,
		isLive: boolean = false
	) {
		super(rootStore, options);
		this.isLive = isLive;
		this.loader = new LoaderStore();
		this.liveOfferMenuStore = liveOfferMenuStore;
		this.favoritesStore = favoritesStore;
		this.setMultiEventViewStore(multiEventViewStore);

		this.mainBettingTypes = this.OfferMapper.getBettingTypes(
			"live",
			betRules
		);

		this.defaultBettingTypes = new Set([
			...this.mainBettingTypes.normal,
			...this.mainBettingTypes.marginal,
		]);
		this.sportStatisticsStore = new SportStatisticsStore(this);
		this.betBoosterEventPicks = new BetBoosterEventPickStore(rootStore);
		this.playerStatisticsStore = new LivePlayerStatisticsStore(this);
		this.playerStatisticsStore = new LivePlayerStatisticsStore(this);
		this.bettingTipsStore = new BettingTipsStore(
			this.rootStore.quickPlayStore
		);
		this.teamStandingsStatsStore = new TeamStandingsStatsStore(this);
		this.teamLineupsStore = new TeamLineupsStore(
			this.playerStatisticsStore
		);
	}

	//#endregion constructor

	//#region expand collapse event

	@action.bound
	toggleShowMore(): void {
		this.isExpanded = !this.isExpanded;
	}

	//#endregion expand collapse event

	//#region actions

	@action.bound
	public setDisplayStatisticsGroup(group: StatisticsGroups | null) {
		if (this.displayStatisticsGroup !== group) {
			// close currently open statistics tab
			this.toggleStatisticsGroup(this.displayStatisticsGroup);
			// open another tab instead
			this.toggleStatisticsGroup(group);

			this.displayStatisticsGroup = group;
		}
	}

	setBetBuilderAvailableTips(tips: any) {
		if (Array.isArray(tips)) {
			this.betBuilderAvailableTips = new Map(
				tips.reduce((acc, tip) => {
					tip.bettingOffers.forEach((bo: any) =>
						acc.push([bo.id, bo])
					);
					return acc;
				}, [])
			);
		} else {
			this.betBuilderAvailableTips.clear();
		}
	}

	@action.bound
	openTab(tab: EventBodyTabs) {
		if (this.currentTab === tab) {
			return;
		}

		// close currently open tab
		switch (this.currentTab) {
			case EventBodyTabs.Statistics:
				this.toggleStatistics(false);
				break;
			case EventBodyTabs.MyBets:
				this.toggleMyBets(false);
				break;
		}

		// open another
		switch (tab) {
			case EventBodyTabs.Statistics:
				this.toggleStatistics(true);
				break;
			case EventBodyTabs.MyBets:
				this.toggleMyBets(true);
				break;
		}

		this.currentTab = tab;
	}

	@action.bound
	toggleStatistics(isToggled: boolean) {
		this.setDisplayStatisticsGroup(
			isToggled ? this.firstAvailableStats : null
		);
	}

	@action.bound
	toggleMyBets(isToggled: boolean) {
		this.rootStore.eventSwitcherViewStore.updateEventsWithBets(
			this.eventId,
			isToggled
		);
		this.handleUrls(isToggled);
	}

	// second arg is never used in this store, but must be present
	// so that the MultiEventViewStore can extend this class
	@action.bound
	handleUrls(myBetsToggleValue: boolean, eventId?: string) {
		this.multiEventViewStore?.handleUrls(myBetsToggleValue, this.eventId);
	}

	@action.bound
	updateEventsMap() {
		this.defaultBettingTypesCount = 0;
		this.events.forEach((event) => {
			// Add sport to event
			// @ts-expect-error
			if (event.sport == null) {
				// @ts-expect-error
				event.sport = this.getMappedSport(event);
			}

			// Add default betting type groups to event
			const eventKeys = this.eventKeysMap.get(event.id);
			// @ts-expect-error
			const favoritesBettingTypesIds = [];
			this.bettingTypeGroups = observable.array();

			// @ts-expect-error
			if (eventKeys?.size > 0) {
				// Loop over all event keys and add there betting type group to event
				// Also add each unique betting type into betting type group
				// @ts-expect-error

				const btGroups = Array.from(eventKeys.values())
					.reduce((acc, eventKey) => {
						//#region  favorites
						// if it's a favorite
						const isFavoriteBettingGroup =
							this.favoritesStore.isUserFavoriteBettingType(
								eventKey.bettingTypeId,
								eventKey.specifier || null
							);
						if (isFavoriteBettingGroup) {
							const lookupGroup =
								this.lookupsStore.bettingTypeGroups.get(
									eventKey?.bettingType?.groupId || ""
								);
							if (
								lookupGroup &&
								// @ts-expect-error
								favoritesBettingTypesIds.find(
									(btGroup) =>
										btGroup.abrv ===
										eventKey.bettingType.groupId
								) == null
							) {
								favoritesBettingTypesIds.push({
									...lookupGroup,
									bettingTypes: [],
								});
							}

							// @ts-expect-error
							const bettingGroup = favoritesBettingTypesIds.find(
								(btGroup) =>
									btGroup.abrv ===
									eventKey.bettingType.groupId
							);
							if (bettingGroup) {
								if (bettingGroup.bettingTypes == null) {
									bettingGroup.bettingTypes = [];
								}

								if (
									bettingGroup.bettingTypes.find(
										// @ts-expect-error
										(bt) => bt.id === eventKey.bettingTypeId
									) == null
								) {
									insert(
										bettingGroup.bettingTypes,
										eventKey.bettingType,
										Sorter.sort((a, b) => {
											const aSort =
												a?.settingsPerSport?.[
													event.sportId
												]?.sortOrder;
											const bSort =
												b?.settingsPerSport?.[
													event.sportId
												]?.sortOrder;

											const aSortOrder =
												aSort == null ? 99999 : aSort;
											const bSortOrder =
												bSort == null ? 99999 : bSort;

											return Math.sign(
												aSortOrder - bSortOrder
											);
										}, "id")
									);
								}
							}
						}

						//#endregion favorites

						//#region insert betting type group
						if (
							acc.find(
								(btGroup) =>
									// @ts-expect-error
									btGroup.abrv ===
									eventKey.bettingType.groupId
							) == null
						) {
							const bettingTypeGroup =
								this.lookupsStore.bettingTypeGroups.get(
									// @ts-expect-error
									eventKey.bettingType.groupId
								);
							if (bettingTypeGroup) {
								/* @ts-expect-error */
								bettingTypeGroup.bettingTypes = [];

								acc.push(
									// @ts-expect-error
									bettingTypeGroup
								);
							}
						}

						//#endregion insert betting type group

						//#region  insert betting type into betting type group

						const bettingGroup = acc.find(
							(btGroup) =>
								// @ts-expect-error
								btGroup.abrv === eventKey.bettingType.groupId
						);

						// @ts-expect-error
						if (bettingGroup && bettingGroup.bettingTypes == null) {
							// @ts-expect-error
							bettingGroup.bettingTypes = [];
						}

						if (eventKey.betBuilder) {
							// @ts-expect-error
							eventKey.bettingType.betBuilder =
								eventKey.betBuilder;
						}

						if (
							this.defaultBettingTypes.has(
								eventKey.bettingType.abrv
							)
						) {
							/* @ts-expect-error */
							eventKey.bettingType.isDefaultBettingType = true;
							this.defaultBettingTypesCount += 1;
						}

						if (
							bettingGroup &&
							// @ts-expect-error
							bettingGroup.bettingTypes.find(
								// @ts-expect-error
								(bt) => bt.id === eventKey.bettingTypeId
							) == null
						) {
							insert(
								// @ts-expect-error
								bettingGroup.bettingTypes,
								eventKey.bettingType,
								Sorter.sort((a, b) => {
									const aSort =
										a?.settingsPerSport?.[event.sportId]
											?.sortOrder;
									const bSort =
										b?.settingsPerSport?.[event.sportId]
											?.sortOrder;

									const aSortOrder =
										aSort == null ? 99999 : aSort;
									const bSortOrder =
										bSort == null ? 99999 : bSort;

									return Math.sign(aSortOrder - bSortOrder);
								}, "id")
							);
						}

						//#endregion insert betting type into betting type group

						return acc;
					}, [])
					.sort(Sorter.sort("sortOrder", "id"));

				this.bettingTypeGroups = observable.array(btGroups);
				// @ts-expect-error
				event.favoritesBettingTypesIds = favoritesBettingTypesIds.sort(
					Sorter.sort("sortOrder", "id")
				);
				this.favoritesBettingTypeGroups = {
					...this.favoritesBettingTypeGroups,
					// @ts-expect-error
					[event.id]: event.favoritesBettingTypesIds,
				};
			}
		});
		return this.events.sort((a, b) => {
			return this.eventIds.indexOf(b.id) - this.eventIds.indexOf(a.id);
		});
	}
	@action.bound
	public async updateEventIds(
		rawEventIds: string,
		period: Period | string = PeriodConverter.GetDefaultPeriod()
	): Promise<void> {
		if (this.eventIdsRaw !== "/" + rawEventIds) {
			const currentRawIds =
				this.eventIdsRaw.charAt(0) === "/"
					? this.eventIdsRaw.split("/").slice(1)
					: this.eventIdsRaw.split("/");

			const newRawIds = rawEventIds.split("/").map((rawId) => {
				const eventId = rawId.split("-")[0];
				const isRawIdFound = currentRawIds.some((item) =>
					item.includes(eventId)
				);

				if (!isRawIdFound) {
					if (!rawId.includes("-myBets=")) {
						rawId += "-myBets=false";
					}
				}

				return rawId;
			});

			this.eventIdsRaw = "/" + newRawIds.join("/");

			const culture = getCurrentCulture();

			if (window.location.pathname.includes("/live/")) {
				redirect(
					`/${culture}/live/${getCurrentLiveSection()}${this.eventIdsRaw}`,
					true
				);
			} else {
				let splittedPathname = window.location.pathname
					.split("/")
					.splice(1);
				splittedPathname[4] = this.eventIdsRaw.replace("/", "");
				redirect(`/${splittedPathname.join("/")}`, true);
			}
		}

		if (!this.isMultiEvent) {
			this.resetDisplayBettingGroup();
		}
	}

	@action.bound
	setMultiEventViewStore(store: MultiEventViewStore | null) {
		this.multiEventViewStore = store;
	}

	//#endregion actions

	//#region offer fetch

	@action.bound
	public async onInitialize(
		id: string,
		period = PeriodConverter.GetDefaultPeriod()
	): Promise<void> {
		this.disposeSubscription();
		this.eventId = id;
		this.period = period;

		//#endregion bet builder

		if (!this.eventIdsRaw && !id) {
			return;
		}

		if (this.eventIdsRaw.includes(DEFAULT_SECTION)) {
			return;
		}
		if (this.isLive) {
			this.initializeLiveOffer(id);
		} else {
			await this.initializePrematchOffer(id, period);
		}

		this.favoritesBettingTypeReaction = reaction(
			() =>
				JSON.stringify(this.favoritesStore.userFavoriteBettingTypesMap),

			() => {
				this.updateEventsMap();
			}
		);

		const isMyBetsOpen =
			!!this.multiEventViewStore?.myBetsToggleStateList.find(
				(event) => event.eventId === id
			)?.isMyBetsOpen;

		if (isMyBetsOpen) {
			this.openTab(EventBodyTabs.MyBets);
		} else {
			this.openTab(EventBodyTabs.Markets);
		}

		this.rootStore.betBuilderStore.addAdditionalOfferStore(this);
	}

	getService(period: Period | string): IOfferService {
		if (PeriodConverter.IsOfferHistoryPeriod(period)) {
			return offerHistoryService;
		}
		return prematchOfferService;
	}
	@action.bound
	async initializePrematchOffer(id: string, period: Period | string) {
		this.onStartFetching();
		this.requestVersion++;
		const currentVersion = this.requestVersion;

		try {
			let filter = {
				filter: {
					id: {
						eq: [id],
					},
				},
			};
			const offer = await this.getService(period).getEventData(filter);

			if (this.requestVersion !== currentVersion) {
				return;
			}

			//@ts-expect-error
			const convertedOffer: OfferSubscriptionResponse = {
				...convertApiOfferResponseToHubModel(offer),
				startingVersion: 0,
			};
			this.handleResponse(convertedOffer, period);
		} catch (err) {
			console.error(err);
			this.eventsMap.clear();
		} finally {
			this.onEndFetching();
		}
	}

	@action.bound
	async initializeLiveOffer(id: string) {
		this.onStartFetching();
		this.requestVersion++;
		const currentVersion = this.requestVersion;

		//#region subscription

		let betOfferFilter: Channels["1"] = {
			name: "betOffer",
		};

		let subscriptionRequest: ISubscriptionRequest = {
			subscriptionId: `event-${id}`,
			compress: WEBPACK_OFFER_COMPRESS,
			channels: [
				{
					name: "event",
					filter: {
						id: {
							eq: [id],
						},
					},
				},
				betOfferFilter,
			],
		};

		//#endregion subscription

		this.subscription = this.rootStore.hub
			.getOfferSubscription(subscriptionRequest)
			.subscribe({
				next: (response) => {
					if (this.requestVersion !== currentVersion) {
						return;
					}
					this.handleResponse(response);
				},
				error: this.handleError,
			});
	}
	/**
	 *
	 * This handls response from hub if you're not
	 */
	@action.bound
	public handleResponse(
		response: OfferSubscriptionResponse,
		period?: string | undefined
	): void {
		const isHistoryOffer =
			period && PeriodConverter.IsOfferHistoryPeriod(period);
		// response.offerChanges.updates?.forEach(
		// 	(e) => (e.event.liveStreamStatus = "live")
		// );

		if (
			this.betBuilderAvailableTips.size > 0 &&
			response.offerChanges &&
			response.offerChanges.updates &&
			response.offerChanges.updates[0] &&
			response.offerChanges.updates[0].offers &&
			response.offerChanges.updates[0].offers.updates
		) {
			const shouldUpdateBetBuilderQuota =
				response.offerChanges.updates[0].offers.updates.some(
					(offer) => {
						if (offer.offers && offer.offers.updates) {
							const matchingOffer = offer.offers.updates.find(
								(o) => this.betBuilderAvailableTips.has(o.id)
							);

							if (matchingOffer) {
								const keyOffer = this.keyOffersMap.get(
									offer.keyId
								);
								if (keyOffer) {
									const tip = keyOffer.get(matchingOffer.id);

									if (tip?.value != matchingOffer.value) {
										return true;
									}
								}
							}
						}
					}
				);

			if (shouldUpdateBetBuilderQuota) {
				this.rootStore.betBuilderStore.updateMultiOfferQuotas([
					this.event,
				]);
			}
		}

		this.assignOfferData(response);
		this.trimEvents(response);
		if (isHistoryOffer) {
			this._historyEventIds = new Set(this.eventsMap.keys());
		}
		const isMulti =
			// @ts-expect-error
			this.multiEventViewStore?.eventsViewMap.size > 1 ||
			this.isMultiEvent;

		if (this.events.length === 0 && !isMulti) {
			this.rootStore?.eventSwitcherViewStore?.removeEventId(this.eventId);

			if (this.rootStore?.eventSwitcherViewStore?.eventIds.length >= 1) {
				/** If requested event is not in offer then we will fetch next one from event switcher.
			 * If event is removed from event switcher and there are 2 or more events left
			 * Don't stop the loader
			 * Set store state to fetching to prevent isEmpty to resolving to true
			+*/
				this.onStartFetching();
				return;
			}
		}

		if (response.startingVersion == 0) {
			if (this.event) {
				this.rootStore.betBuilderStore.onAdditionalStoreEnter(
					this.event
				);
				this.sportStatisticsStore.fetchWinLostDrawStats(this.event);
			}

			if (this.sport?.abrv) {
				this.sportStatisticsStore.checkLiveStatisticsAvailability();
				this.playerStatisticsStore.checkLiveStatisticsAvailability();
				this.teamLineupsStore.checkPrematchTeamLineupsAvailability();
			}
			if (this.event && this.sport && this.tournament) {
				analyticsTracker.trackMatchViewed(
					this.event.id,
					this.sport.abrv,
					this.teamOne?.abrv || "",
					this.teamTwo?.abrv || "",
					this.tournament.abrv
				);
			}
		}
		if (response.startingVersion == 0 || response.lookups?.bettingTypes) {
			this.sportStatisticsStore.checkBettingTypeStatisticsAvailability();
		}

		if (response.startingVersion == 0) {
			this.sportStatisticsStore.checkStreakStatsAvailability();
			this.sportStatisticsStore.checkEventInsightsAvailability();
			this.teamStandingsStatsStore.initializeLiveTeamStandingsStatistics();
		}
		if (!isHistoryOffer) {
			runInAction(async () => {
				if (this.event?.betBuilder) {
					if (
						this.betBoosterEventPicks.previousBetBuilderStatus !==
						this.event.betBuilder
					) {
						this.betBoosterEventPicks.previousBetBuilderStatus =
							this.event?.betBuilder || false;
						if (
							this.eventId &&
							!this.betBoosterEventPicks.isAvailable
						) {
							await this.betBoosterEventPicks.onInitialize(
								this.eventId,
								this.event.isLive
							);
						}
					}
				} else {
					if (this.betBoosterEventPicks.isAvailable) {
						this.betBoosterEventPicks.onDispose();
					}
				}
				if (response.startingVersion == 0) {
					const isBettingTipsFeatureEnabled =
						THEME_SETTINGS.features.quickPlayOptions?.includes(
							"betting-tips"
						);
					if (isBettingTipsFeatureEnabled && this.event?.id) {
						await this.bettingTipsStore.onInitialized({
							eventId: this.event.id,
						});
					}
				}
			});
		}
		this.onEndFetching();
		this.updateEventsMap();
	}

	@action.bound
	public handleError(err: any): void {
		console.error(err);
		this.onEndFetching();
	}

	@action.bound
	public onStartFetching(): void {
		if (!this.isFetchingData) {
			this.loader.suspend();
			this.isFetchingData = true;
		}
	}

	@action.bound
	public onEndFetching(): void {
		this.onBeforeFetchEnd();
		this.loader.resume();
		this.isFetchingData = false;
		this.isStoreInitialized = true;
	}

	@action.bound
	protected onBeforeFetchEnd(): void {
		this.liveOfferMenuStore?.rememberSelection();
	}

	@action.bound
	private trimEvents(data: OfferSubscriptionResponse) {
		if (data.startingVersion !== 0) {
			return;
		}
		const eventIds = this.multiEventViewStore?.eventIds || this.eventIds;
		for (const [eventId] of this.eventsMap) {
			// If event is in selected events then don't remove
			if (
				!eventIds.includes(eventId) ||
				!data.offerChanges?.updates?.some((e) => e.eventId === eventId)
			) {
				this.eventsMap.delete(eventId);

				// Remove offer
				this.eventKeysMap
					.get(eventId)
					?.forEach((_, keyId) => this.keyOffersMap.delete(keyId));

				this.eventKeysMap.delete(eventId);
				continue;
			}

			// This will remove offer that is no longer showed
			for (const [eventId, eventKey] of this.eventKeysMap) {
				const eventUpdate = data.offerChanges.updates.find(
					(e) => e.eventId === eventId
				);

				eventKey.forEach((eventKeyOffer, keyId) => {
					if (
						!eventUpdate?.offers?.updates?.some(
							(u) => u.keyId === eventKeyOffer.id
						)
					) {
						this.keyOffersMap.delete(keyId);
					}
					eventIds;
				});
			}
		}
	}

	//#endregion offer fetch

	//#region disposers

	@action.bound
	public onDispose() {
		this.rootStore.betBuilderStore.removeAdditionalOfferStore({
			id: this.event?.id || this.eventId,
		});
		this.eventIdsRaw = "";
		this.favoritesBettingTypeGroups = {};
		this.resetDisplayBettingGroup();
		this.isStoreInitialized = false;
		this.disposeSubscription();
		this.reset();
		this.sportStatisticsStore.onDispose();
		this.betBoosterEventPicks.onDispose();
		this.playerStatisticsStore.onDispose();
		this.disposeReaction();
		this.defaultBettingTypes.clear();
		this.defaultBettingTypesCount = 0;
		this.mainBettingTypes = null;
		this.bettingTypeGroups = observable.array();
		this.isExpanded = false;
		this.betBuilderAvailableTips.clear();
		this.bettingTipsStore.onDispose();
		this.isLive = false;
		this.teamStandingsStatsStore.onDispose();
		this.teamLineupsStore.onDispose();
		this._historyEventIds.clear();
	}

	@action.bound
	disposeSubscription() {
		if (this.subscription != null) {
			this.subscription.unsubscribe();
			this.subscription = null;
		}
	}

	@action.bound
	disposeReaction() {
		//maybe it works
		if (this.favoritesBettingTypeReaction != null) {
			this.favoritesBettingTypeReaction();
			this.favoritesBettingTypeReaction = null;
		}
	}
	//#endregion disposers

	/* Helpers
	
	*/
	@computed get teamOne(): Team | undefined {
		if (
			this.event &&
			this.event.displayValues &&
			this.event.displayValues.teamOneId
		) {
			return this.lookupsStore.teams.get(
				this.event.displayValues.teamOneId
			);
		}
		return undefined;
	}

	@computed get teamTwo(): Team | undefined {
		if (
			this.event &&
			this.event.displayValues &&
			this.event.displayValues.teamTwoId
		) {
			return this.lookupsStore.teams.get(
				this.event.displayValues.teamTwoId
			);
		}
		return undefined;
	}

	@computed get event(): EventOffer | undefined {
		return this.eventsMap.get(this.eventId);
	}

	get sport(): Sport | undefined {
		return this.lookupsStore.sports.get(this.event?.sportId || "");
	}

	get sportCategory() {
		return this.lookupsStore.categories.get(
			this.event?.sportCategoryId || ""
		);
	}

	get tournament(): Tournament | undefined {
		return this.lookupsStore.tournaments.get(
			this.event?.tournamentId || ""
		);
	}

	@computed
	get availableStatisticsGroups(): StatisticsGroups[] {
		const groups: StatisticsGroups[] = [];
		if (this.sportStatisticsStore.isMatchStatsAvailable) {
			groups.push(StatisticsGroups.LiveStats);
		}
		if (this.sportStatisticsStore.isStreakStatAvailable) {
			groups.push(StatisticsGroups.StreakStats);
		}
		if (this.teamLineupsStore.isTeamLineupsAvailable) {
			groups.push(StatisticsGroups.TeamLineups);
		}
		if (this.playerStatisticsStore.isPlayerStatsAvailable) {
			groups.push(StatisticsGroups.PlayerStats);
		}
		if (this.sportStatisticsStore.isEventInsightsAvailable) {
			groups.push(StatisticsGroups.EventInsights);
		}
		if (this.teamStandingsStatsStore.isTeamStandingsStatsAvailable) {
			groups.push(StatisticsGroups.TeamStandings);
		}

		return groups;
	}

	@computed
	get shouldShowStatisticsHeader(): boolean {
		return this.availableStatisticsGroups.length > 1;
	}

	protected toggleStatisticsGroup(group: StatisticsGroups | null) {
		switch (group) {
			case StatisticsGroups.TeamLineups:
				this.teamLineupsStore.toggleTeamLineups();
				break;
			case StatisticsGroups.StreakStats:
				this.sportStatisticsStore.toggleStreakStats();
				break;
			case StatisticsGroups.LiveStats:
				this.sportStatisticsStore.toggleMatchStat();
				break;
			case StatisticsGroups.PlayerStats:
				this.playerStatisticsStore.togglePlayerStat();
				break;
			case StatisticsGroups.FullStats:
				this.sportStatisticsStore.toggleFullStat();
				break;
			case StatisticsGroups.TeamStandings:
				this.teamStandingsStatsStore.toggleTeamStandingsStats();
				break;
		}
	}
}
